import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import { compose, withProps } from "recompose";
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
  DirectionsRenderer,
} from "react-google-maps";
import EmpresaContext from "../../../contexts/Empresa";
import api from "../../../services/api";
import AuthContext from "../../../contexts/Auth";

// Componente do mapa com Google Maps
const GOOGLE_MAPS_API_KEY = process?.env?.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const MapComponent = compose(
  withProps({
    googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=3.exp&libraries=geometry,drawing,places`,
    loadingElement: <div style={{ height: `100%` }} />,
    containerElement: <div style={{ height: `500px` }} />,
    mapElement: <div style={{ height: `100%` }} />,
  }),
  withScriptjs,
  withGoogleMap
)((props) => (
  <GoogleMap
    defaultZoom={10}
    defaultCenter={{ lat: -23.5505, lng: -46.6333 }} // São Paulo como padrão
    center={props.center}
    zoom={props.zoom}
  >
    {/* Marcadores das oportunidades */}
    {props.markers &&
      props.markers.map((marker, index) => (
        <Marker
          key={index}
          position={{ lat: marker.lat, lng: marker.lng }}
          label={`${index + 1}`}
          title={marker.title}
        />
      ))}

    {/* Renderizar a rota se disponível */}
    {props.directions && <DirectionsRenderer directions={props.directions} />}
  </GoogleMap>
));

export default function RotasVisitas({
  show,
  onClose,
  oportunidades = [],
  notify,
}) {
  const { empresaSelecionada } = useContext(EmpresaContext);
  const { auth } = useContext(AuthContext);

  // Estados do componente
  const [numeroVisitas, setNumeroVisitas] = useState(1);
  const [pontoPartida, setPontoPartida] = useState("escritorio");
  const [enderecoPersonalizado, setEnderecoPersonalizado] = useState("");
  const [destinoFinal, setDestinoFinal] = useState("mesmo");
  const [enderecoDestino, setEnderecoDestino] = useState("");
  const [loading, setLoading] = useState(false);
  const [directions, setDirections] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [rotaCalculada, setRotaCalculada] = useState(false);
  const [center, setCenter] = useState({ lat: -23.5505, lng: -46.6333 });
  const [zoom, setZoom] = useState(10);

  // Estados para autocomplete
  const [sugestoesPartida, setSugestoesPartida] = useState([]);
  const [sugestoesDestino, setSugestoesDestino] = useState([]);
  const [mostrarSugestoesPartida, setMostrarSugestoesPartida] = useState(false);
  const [mostrarSugestoesDestino, setMostrarSugestoesDestino] = useState(false);

  // Estados para registro da rota
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [observacoesRota, setObservacoesRota] = useState("");
  const [colaboradores, setColaboradores] = useState([]);
  const [loadingColaboradores, setLoadingColaboradores] = useState(false);
  const [tempoEstimadoTotal, setTempoEstimadoTotal] = useState(0);
  const [salvandoRota, setSalvandoRota] = useState(false);

  // Seleção de imóvel por oportunidade e coordenadas usadas
  const [oportunidadesSelecionadasState, setOportunidadesSelecionadasState] = useState([]);
  const [imoveisSelecionadosPorOp, setImoveisSelecionadosPorOp] = useState({});
  const [coordsPartidaState, setCoordsPartidaState] = useState(null);
  const [coordsDestinoState, setCoordsDestinoState] = useState(null);

  // Refs para os campos de input
  const inputPartidaRef = useRef(null);
  const inputDestinoRef = useRef(null);

  // Estado para armazenar oportunidades com imóveis válidos
  const [oportunidadesComLocalizacao, setOportunidadesComLocalizacao] = useState([]);
  const [maxVisitas, setMaxVisitas] = useState(0);

  // Buscar imóveis e filtrar oportunidades quando o modal abrir
  useEffect(() => {
    const filtrarOportunidadesComImoveis = async () => {
      if (!show || !oportunidades.data || !empresaSelecionada?.id) {
        setOportunidadesComLocalizacao([]);
        setMaxVisitas(0);
        return;
      }

      try {
        const oportunidadesValidas = [];

        for (const op of oportunidades.data) {
          if (!op.cliente_id) continue;

          try {
            const response = await api.get(
              `/common/empresas/${empresaSelecionada.id}/clientes/${op.cliente_id}/imoveis`,
              {
                headers: {
                  Authorization: `Bearer ${auth.token}`,
                },
              }
            );

            const imoveisData = response.data.data || response.data;
            const imoveisComCoordenadas = imoveisData.filter(
              (imovel) => imovel.latitude && imovel.longitude
            );

            if (imoveisComCoordenadas.length > 0) {
              oportunidadesValidas.push(op);
            }
          } catch (error) {
            console.error(`Erro ao buscar imóveis do cliente ${op.cliente_id}:`, error);
          }
        }

        setOportunidadesComLocalizacao(oportunidadesValidas);
        setMaxVisitas(oportunidadesValidas.length);
      } catch (error) {
        console.error("Erro ao filtrar oportunidades:", error);
        setOportunidadesComLocalizacao([]);
        setMaxVisitas(0);
      }
    };

    filtrarOportunidadesComImoveis();
  }, [show, oportunidades.data, auth.token, empresaSelecionada?.id]);

  useEffect(() => {
    if (show) {
      // Reset do estado quando o modal abre
      setNumeroVisitas(Math.min(5, maxVisitas));
      setPontoPartida("escritorio");
      setEnderecoPersonalizado("");
      setDestinoFinal("mesmo");
      setEnderecoDestino("");
      setDirections(null);
      setMarkers([]);
      setRotaCalculada(false);
      setLoading(false);
      // Limpar sugestões de autocomplete
      setSugestoesPartida([]);
      setSugestoesDestino([]);
      setMostrarSugestoesPartida(false);
      setMostrarSugestoesDestino(false);

      // Reset campos de registro
      setColaboradorSelecionado("");
      setDataInicio("");
      setDataFim("");
      setObservacoesRota("");
      setTempoEstimadoTotal(0);
      setSalvandoRota(false);

      // Buscar colaboradores
      buscarColaboradores();
    }
  }, [show, maxVisitas]);

  const buscarColaboradores = async () => {
    if (!empresaSelecionada?.id) return;

    setLoadingColaboradores(true);
    try {
      const response = await api.get(
        `/common/empresas/${empresaSelecionada.id}/colaboradores?situacaoNaEmpresa=A`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );
      setColaboradores(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar colaboradores:", error);
      notify("danger", "Erro ao carregar colaboradores");
      setColaboradores([]);
    } finally {
      setLoadingColaboradores(false);
    }
  };



  // Função para formatar tempo em formato legível
  const formatarTempo = (minutos) => {
    if (!minutos || minutos === 0) return "0min";

    const horas = Math.floor(minutos / 60);
    const minutosRestantes = minutos % 60;

    if (horas === 0) {
      return `${minutosRestantes}min`;
    } else if (minutosRestantes === 0) {
      return `${horas}h`;
    } else {
      return `${horas}h${minutosRestantes}min`;
    }
  };

  // Função removida - URL do Google Maps agora é gerada pelo backend

  // Função para calcular distância euclidiana simples
  const calcularDistancia = (lat1, lng1, lat2, lng2) => {
    const dx = lat1 - lat2;
    const dy = lng1 - lng2;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Função para buscar sugestões de endereços usando Google Places API
  const buscarSugestoesEndereco = async (input, setSugestoes) => {
    if (!input || input.length < 3) {
      setSugestoes([]);
      return;
    }

    try {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: input,
          componentRestrictions: { country: "br" }, // Restringir ao Brasil
          types: ["address"], // Focar em endereços
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSugestoes(predictions.slice(0, 5)); // Limitar a 5 sugestões
          } else {
            setSugestoes([]);
          }
        }
      );
    } catch (error) {
      console.error("Erro ao buscar sugestões:", error);
      setSugestoes([]);
    }
  };

  // Função para obter coordenadas do endereço usando Geocoding API
  const geocodificarEndereco = async (endereco) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      return new Promise((resolve, reject) => {
        geocoder.geocode({ address: endereco }, (results, status) => {
          if (status === "OK" && results[0]) {
            const location = results[0].geometry.location;
            resolve({
              lat: location.lat(),
              lng: location.lng(),
            });
          } else {
            reject(new Error("Endereço não encontrado"));
          }
        });
      });
    } catch (error) {
      throw new Error("Erro ao geocodificar endereço");
    }
  };

  // Função para montar/pré-visualizar a rota
  const montarRota = async () => {
    if (maxVisitas === 0) {
      notify("warning", "Nenhuma oportunidade com localização encontrada");
      return;
    }

    if (numeroVisitas > maxVisitas) {
      notify("warning", `Máximo de ${maxVisitas} visitas disponíveis`);
      return;
    }

    setLoading(true);
    setRotaCalculada(false);

    try {
      // 1. Definir ponto de partida
      let coordenadasPartida;
      if (pontoPartida === "escritorio") {
        // Usar endereço da empresa (simplificado - usando São Paulo como exemplo)
        coordenadasPartida = { lat: -23.5505, lng: -46.6333 };
      } else {
        if (!enderecoPersonalizado.trim()) {
          notify("warning", "Informe o endereço personalizado");
          setLoading(false);
          return;
        }
        coordenadasPartida = await geocodificarEndereco(enderecoPersonalizado);
      }

      // 2. Buscar imóveis e calcular distâncias
      const oportunidadesComDistancia = [];

      for (const op of oportunidadesComLocalizacao) {
        try {
          const response = await api.get(
            `/common/empresas/${empresaSelecionada.id}/clientes/${op.cliente_id}/imoveis`,
            {
              headers: {
                Authorization: `Bearer ${auth.token}`,
              },
            }
          );

          const imoveisData = response.data.data || response.data;
          const imoveisComCoordenadas = imoveisData.filter(
            (imovel) => imovel.latitude && imovel.longitude
          );

          if (imoveisComCoordenadas.length > 0) {
            const primeiroImovel = imoveisComCoordenadas[0];
            const distancia = calcularDistancia(
              coordenadasPartida.lat,
              coordenadasPartida.lng,
              parseFloat(primeiroImovel.latitude),
              parseFloat(primeiroImovel.longitude)
            );

            oportunidadesComDistancia.push({
              ...op,
              distancia,
              imoveis: imoveisComCoordenadas
            });
          }
        } catch (error) {
          console.error(`Erro ao buscar imóveis para oportunidade ${op.id}:`, error);
        }
      }

      const oportunidadesSelecionadas = oportunidadesComDistancia
        .sort((a, b) => a.distancia - b.distancia)
        .slice(0, numeroVisitas);

      // 3. Preparar seleção de imóveis (padrão: todos os imóveis do cliente)
      const selecaoLocal = {};
      oportunidadesSelecionadas.forEach(op => {
        if (op.imoveis && op.imoveis.length > 0) {
          selecaoLocal[op.id] = op.imoveis.map(i => i.id);
        }
      });
      setImoveisSelecionadosPorOp(selecaoLocal);
      setOportunidadesSelecionadasState(oportunidadesSelecionadas);

      // Waypoints: um por imóvel selecionado
      const waypoints = [];
      oportunidadesSelecionadas.forEach((op) => {
        const ids = (selecaoLocal[op.id] && selecaoLocal[op.id].length) ? selecaoLocal[op.id] : (op.imoveis[0] ? [op.imoveis[0].id] : []);
        ids.forEach((id) => {
          const im = op.imoveis.find(i => i.id === id) || op.imoveis[0];
          waypoints.push({
            location: { lat: parseFloat(im.latitude), lng: parseFloat(im.longitude) },
            stopover: true,
          });
        });
      });

      // 4. Definir destino final

      let coordenadasDestino;
      if (destinoFinal === "mesmo") {
        coordenadasDestino = coordenadasPartida;
      } else {
        if (!enderecoDestino.trim()) {
          notify("warning", "Informe o endereço de destino");
          setLoading(false);
          return;
        }
        coordenadasDestino = await geocodificarEndereco(enderecoDestino);
      }

      // 5. Calcular rota otimizada usando Google Maps Directions API
      const directionsService = new window.google.maps.DirectionsService();


      // Persistir coordenadas para recalcular a rota ao trocar de imóvel
      setCoordsPartidaState(coordenadasPartida);
      setCoordsDestinoState(coordenadasDestino);

      directionsService.route(

        {
          origin: coordenadasPartida,
          destination: coordenadasDestino,
          waypoints: waypoints,
          optimizeWaypoints: true, // Otimizar ordem dos waypoints
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === "OK") {
            setDirections(result);

            // Calcular tempo estimado total
            let tempoDeslocamento = 0;
            if (result.routes && result.routes[0] && result.routes[0].legs) {
              tempoDeslocamento = result.routes[0].legs.reduce((total, leg) => {
                return total + (leg.duration ? leg.duration.value / 60 : 0); // Converter segundos para minutos
              }, 0);
            }

            const tempoVisitas = waypoints.length * 60; // 60 minutos por visita
            const tempoTotal = Math.round(tempoDeslocamento + tempoVisitas);
            setTempoEstimadoTotal(tempoTotal);

            // Criar marcadores para exibição (usando imóvel selecionado)
            const novosMarkers = [
              {
                lat: coordenadasPartida.lat,
                lng: coordenadasPartida.lng,
                title: "Ponto de Partida",
              },
            ];
            let contador = 1;
            oportunidadesSelecionadas.forEach((op) => {
              const ids = (selecaoLocal[op.id] && selecaoLocal[op.id].length) ? selecaoLocal[op.id] : (op.imoveis[0] ? [op.imoveis[0].id] : []);
              ids.forEach((id) => {
                const im = op.imoveis.find(i => i.id === id) || op.imoveis[0];
                novosMarkers.push({
                  lat: parseFloat(im.latitude),
                  lng: parseFloat(im.longitude),
                  title: `${contador++}. ${im.nome || 'Imóvel'} - ${op.cliente?.nome || 'Cliente'}`,
                });
              });
            });

            if (destinoFinal !== "mesmo") {
              novosMarkers.push({
                lat: coordenadasDestino.lat,
                lng: coordenadasDestino.lng,
                title: "Destino Final",
              });
            }

            setMarkers(novosMarkers);
            setRotaCalculada(true);

            // Ajustar centro e zoom do mapa
            const bounds = new window.google.maps.LatLngBounds();
            novosMarkers.forEach((marker) => {
              bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
            });

            notify("success", "Rota calculada com sucesso!");
          } else {
            console.error("Erro ao calcular rota:", status);
            notify("danger", "Erro ao calcular rota. Tente novamente.");
          }
          setLoading(false);
        }
      );
    } catch (error) {
      console.error("Erro:", error);
      notify("danger", error.message || "Erro ao processar rota");
      setLoading(false);
    }
  };

  // Funções para manipular autocomplete
  const handleEnderecoPartidaChange = (e) => {
    const valor = e.target.value;
    setEnderecoPersonalizado(valor);
    buscarSugestoesEndereco(valor, setSugestoesPartida);
    setMostrarSugestoesPartida(true);
  };

  const handleEnderecoDestinoChange = (e) => {
    const valor = e.target.value;
    setEnderecoDestino(valor);
    buscarSugestoesEndereco(valor, setSugestoesDestino);
    setMostrarSugestoesDestino(true);
  };

  const selecionarSugestaoPartida = (sugestao) => {
    setEnderecoPersonalizado(sugestao.description);
    setSugestoesPartida([]);
    setMostrarSugestoesPartida(false);
  };

  const selecionarSugestaoDestino = (sugestao) => {
    setEnderecoDestino(sugestao.description);
    setSugestoesDestino([]);
    setMostrarSugestoesDestino(false);
  };


  // Alterna seleção de imóvel (múltiplos por oportunidade) e recalcula rota
  const onToggleImovel = async (opId, imovelId, checked) => {
    const atual = { ...imoveisSelecionadosPorOp };
    const lista = new Set(atual[opId] || []);
    const idNum = parseInt(imovelId, 10);
    if (checked) lista.add(idNum); else lista.delete(idNum);
    atual[opId] = Array.from(lista);
    setImoveisSelecionadosPorOp(atual);

    if (!coordsPartidaState || !coordsDestinoState || oportunidadesSelecionadasState.length === 0) return;

    const waypoints = [];
    oportunidadesSelecionadasState.forEach((op) => {
      const ids = (atual[op.id] && atual[op.id].length) ? atual[op.id] : (op.imoveis[0] ? [op.imoveis[0].id] : []);
      ids.forEach((id) => {
        const im = op.imoveis.find((i) => i.id === id) || op.imoveis[0];
        waypoints.push({
          location: { lat: parseFloat(im.latitude), lng: parseFloat(im.longitude) },
          stopover: true,
        });
      });
    });

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: coordsPartidaState,
        destination: coordsDestinoState,
        waypoints,
        optimizeWaypoints: true,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);

          let tempoDeslocamento = 0;
          if (result.routes && result.routes[0] && result.routes[0].legs) {
            tempoDeslocamento = result.routes[0].legs.reduce((total, leg) => total + (leg.duration ? leg.duration.value / 60 : 0), 0);
          }
          const tempoVisitas = waypoints.length * 60;
          setTempoEstimadoTotal(Math.round(tempoDeslocamento + tempoVisitas));

          const novosMarkers = [{ lat: coordsPartidaState.lat, lng: coordsPartidaState.lng, title: "Ponto de Partida" }];
          let contador = 1;
          oportunidadesSelecionadasState.forEach((op) => {
            const ids = (atual[op.id] && atual[op.id].length) ? atual[op.id] : (op.imoveis[0] ? [op.imoveis[0].id] : []);
            ids.forEach((id) => {
              const im = op.imoveis.find((i) => i.id === id) || op.imoveis[0];
              novosMarkers.push({
                lat: parseFloat(im.latitude),
                lng: parseFloat(im.longitude),
                title: `${contador++}. ${im.nome || 'Imóvel'} - ${op.cliente?.nome || 'Cliente'}`,
              });
            });
          });
          if (destinoFinal !== "mesmo") {
            novosMarkers.push({ lat: coordsDestinoState.lat, lng: coordsDestinoState.lng, title: "Destino Final" });
          }
          setMarkers(novosMarkers);
          setRotaCalculada(true);
        } else {
          notify("danger", "Não foi possível recalcular a rota com a seleção de imóveis.");
        }
      }
    );
  };

  // Função para registrar a rota no banco de dados
  const registrarRota = async () => {
    // Validações
    if (!colaboradorSelecionado) {
      notify("warning", "Selecione um colaborador para registrar a rota");
      return;
    }

    if (!dataInicio) {
      notify("warning", "Informe a data de início da rota");
      return;
    }

    if (dataFim && dataFim < dataInicio) {
      notify("warning", "A data final deve ser igual ou posterior à data de início");
      return;
    }

    setSalvandoRota(true);

    try {
      // Buscar imóveis e preparar dados das oportunidades selecionadas
      const coordenadasPartidaCalculo = pontoPartida === "escritorio"
        ? { lat: -23.5505, lng: -46.6333 }
        : await geocodificarEndereco(enderecoPersonalizado);

      const oportunidadesComImoveisEDistancia = [];

      for (const op of oportunidadesComLocalizacao) {
        try {
          const response = await api.get(
            `/common/empresas/${empresaSelecionada.id}/clientes/${op.cliente_id}/imoveis`,
            {
              headers: {
                Authorization: `Bearer ${auth.token}`,
              },
            }
          );

          const imoveisData = response.data.data || response.data;
          const imoveisComCoordenadas = imoveisData.filter(
            (imovel) => imovel.latitude && imovel.longitude
          );

          if (imoveisComCoordenadas.length > 0) {
            const primeiroImovel = imoveisComCoordenadas[0];
            const distancia = calcularDistancia(
              coordenadasPartidaCalculo.lat,
              coordenadasPartidaCalculo.lng,
              parseFloat(primeiroImovel.latitude),
              parseFloat(primeiroImovel.longitude)
            );

            oportunidadesComImoveisEDistancia.push({
              ...op,
              distancia,
              imoveis: imoveisComCoordenadas
            });
          }
        } catch (error) {
          console.error(`Erro ao buscar imóveis para oportunidade ${op.id}:`, error);
        }
      }

      const oportunidadesOrdenadas = oportunidadesComImoveisEDistancia
        .sort((a, b) => a.distancia - b.distancia)
        .slice(0, numeroVisitas);

      const oportunidadesSelecionadas = oportunidadesOrdenadas.map((op, index) => ({
        id: op.id,
        tempo_estimado_minutos: 60,
        ordem: index + 1,
        observacoes_visita: `Visita para ${op.cliente?.nome || 'Cliente'} - ${op.nome || 'Oportunidade'}`
      }));


      // Aplicar sele e7 e3o de im f3vel feita no preview (quando houver)
      const oportunidadesSelecionadasPayload = (oportunidadesSelecionadasState && oportunidadesSelecionadasState.length)
        ? oportunidadesSelecionadasState.flatMap((op, indexBase) => {
          const ids = (imoveisSelecionadosPorOp[op.id] && imoveisSelecionadosPorOp[op.id].length)
            ? imoveisSelecionadosPorOp[op.id]
            : (op.imoveis || []).map(i => i.id);
          return ids.map((id) => ({
            id: op.id,
            imovel_id: id,
            tempo_estimado_minutos: 60,
            ordem: indexBase + 1,
            observacoes_visita: `Visita para ${op.cliente?.nome || 'Cliente'} - ${op.nome || 'Oportunidade'}`
          }));
        })
        : oportunidadesSelecionadas;

      // Obter coordenadas dos pontos de partida e destino
      let coordenadasPartida = { lat: -23.5505, lng: -46.6333 }; // São Paulo padrão
      let coordenadasDestino = null;

      try {
        // Coordenadas do ponto de partida
        if (pontoPartida === "personalizado" && enderecoPersonalizado) {
          coordenadasPartida = await geocodificarEndereco(enderecoPersonalizado);
        }

        // Coordenadas do destino (se diferente do ponto de partida)
        if (destinoFinal === "personalizado" && enderecoDestino) {
          coordenadasDestino = await geocodificarEndereco(enderecoDestino);
        }
      } catch (error) {
        console.warn("Erro ao geocodificar endereços:", error);
        // Continua com coordenadas padrão
      }


      // Preparar dados completos da rota para backup
      const dadosRotaCompletos = {
        configuracao: {
          numeroVisitas,
          pontoPartida,
          enderecoPersonalizado,
          destinoFinal,
          enderecoDestino
        },
        coordenadas: {
          partida: coordenadasPartida,
          destino: coordenadasDestino
        },
        oportunidades: oportunidadesOrdenadas.flatMap(op => {
          const ids = (imoveisSelecionadosPorOp[op.id] && imoveisSelecionadosPorOp[op.id].length)
            ? imoveisSelecionadosPorOp[op.id]
            : (op.imoveis || []).map(i => i.id);
          return ids.map(id => ({
            id: op.id,
            nome: op.nome,
            imovel_id: id,
            cliente: {
              id: op.cliente.id,
              nome: op.cliente.nome,
              latitude: op.cliente.latitude,
              longitude: op.cliente.longitude
            }
          }))
        }),
        tempoEstimado: {
          total: tempoEstimadoTotal,
          deslocamento: tempoEstimadoTotal - (numeroVisitas * 60),
          visitas: numeroVisitas * 60
        },
        dataCalculo: new Date().toISOString()
      };

      // Preparar dados da rota
      const dadosRota = {
        colaborador_id: parseInt(colaboradorSelecionado),
        data_inicio: dataInicio,
        data_fim: dataFim || dataInicio,
        observacoes: observacoesRota,
        tempo_estimado_minutos: tempoEstimadoTotal,
        oportunidades: oportunidadesSelecionadasPayload,

        // Dados de geolocalização
        endereco_partida: pontoPartida === "personalizado" ? enderecoPersonalizado : "Escritório da empresa",
        latitude_partida: coordenadasPartida.lat,
        longitude_partida: coordenadasPartida.lng,

        endereco_destino: destinoFinal === "personalizado" ? enderecoDestino : (destinoFinal === "mesmo" ? "Mesmo do ponto de partida" : null),
        latitude_destino: coordenadasDestino ? coordenadasDestino.lat : (destinoFinal === "mesmo" ? coordenadasPartida.lat : null),
        longitude_destino: coordenadasDestino ? coordenadasDestino.lng : (destinoFinal === "mesmo" ? coordenadasPartida.lng : null),

        dados_rota_json: dadosRotaCompletos
      };

      // Enviar para a API
      const response = await api.post(
        `/common/empresas/${empresaSelecionada.id}/rotas-visitas`,
        dadosRota,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );

      const rotaId = response.data?.data?.id;
      notify("success", `Rota registrada com sucesso! ${rotaId ? `ID: ${rotaId}` : ''}`);

      const rotaCriada = response.data?.data;
      if (rotaCriada?.google_maps_url && window.confirm("Deseja abrir a rota no Google Maps?")) {
        window.open(rotaCriada.google_maps_url, '_blank');
      }

      onClose();

    } catch (error) {
      console.error("Erro ao registrar rota:", error);

      if (error.response?.data?.errors) {
        // Mostrar erros de validação
        const primeiroErro = Object.values(error.response.data.errors)[0];
        notify("danger", Array.isArray(primeiroErro) ? primeiroErro[0] : primeiroErro);
      } else if (error.response?.data?.message) {
        notify("danger", error.response.data.message);
      } else {
        notify("danger", "Erro ao registrar rota. Tente novamente.");
      }
    } finally {
      setSalvandoRota(false);
    }
  };

  return (
    <Modal isOpen={show} toggle={onClose} size="xl" style={{ maxWidth: "95%" }}>
      <ModalHeader toggle={onClose}>
        Criar Rota de Visitas
        {maxVisitas > 0 && (
          <small className="text-muted ml-2">
            ({maxVisitas} oportunidades com localização disponíveis)
          </small>
        )}
      </ModalHeader>
      <ModalBody>
        {maxVisitas === 0 ? (
          <Alert color="warning">
            <strong>Nenhuma oportunidade com localização encontrada!</strong>
            <br />
            Para criar rotas, é necessário que as oportunidades tenham clientes
            com coordenadas de latitude e longitude cadastradas.
          </Alert>
        ) : (
          <Row>
            {/* Painel de Configuração */}
            <Col md="4">
              <div className="border-right pr-3">
                <h5>Configuração da Rota</h5>

                {/* Número de clientes */}
                <FormGroup>
                  <Label>Número de clientes a visitar *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={maxVisitas}
                    value={numeroVisitas}
                    onChange={(e) => setNumeroVisitas(parseInt(e.target.value) || 1)}
                  />
                  <small className="text-muted">
                    Máximo: {maxVisitas} clientes disponíveis
                  </small>
                </FormGroup>

                {/* Ponto de partida */}
                <FormGroup>
                  <Label>Ponto de partida</Label>
                  <Input
                    type="select"
                    value={pontoPartida}
                    onChange={(e) => setPontoPartida(e.target.value)}
                  >
                    <option value="escritorio">Escritório da empresa</option>
                    <option value="personalizado">Endereço personalizado</option>
                  </Input>
                </FormGroup>

                {pontoPartida === "personalizado" && (
                  <FormGroup style={{ position: "relative" }}>
                    <Label>Endereço personalizado</Label>
                    <Input
                      innerRef={inputPartidaRef}
                      type="text"
                      placeholder="Digite o endereço completo"
                      value={enderecoPersonalizado}
                      onChange={handleEnderecoPartidaChange}
                      onFocus={() => setMostrarSugestoesPartida(true)}
                      onBlur={() => {
                        // Delay para permitir clique nas sugestões
                        setTimeout(() => setMostrarSugestoesPartida(false), 200);
                      }}
                    />
                    {mostrarSugestoesPartida && sugestoesPartida.length > 0 && (
                      <ListGroup
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          maxHeight: "200px",
                          overflowY: "auto",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          backgroundColor: "white",
                        }}
                      >
                        {sugestoesPartida.map((sugestao, index) => (
                          <ListGroupItem
                            key={index}
                            action
                            onClick={() => selecionarSugestaoPartida(sugestao)}
                            style={{ cursor: "pointer", padding: "8px 12px" }}
                          >
                            <div style={{ fontSize: "14px", fontWeight: "500" }}>
                              {sugestao.structured_formatting?.main_text || sugestao.description}
                            </div>
                            <div style={{ fontSize: "12px", color: "#666" }}>
                              {sugestao.structured_formatting?.secondary_text || ""}
                            </div>
                          </ListGroupItem>
                        ))}
                      </ListGroup>
                    )}
                  </FormGroup>
                )}

                {/* Destino final */}
                <FormGroup>
                  <Label>Destino final</Label>
                  <Input
                    type="select"
                    value={destinoFinal}
                    onChange={(e) => setDestinoFinal(e.target.value)}
                  >
                    <option value="mesmo">Mesmo do ponto de partida</option>
                    <option value="personalizado">Endereço personalizado</option>
                  </Input>
                </FormGroup>

                {destinoFinal === "personalizado" && (
                  <FormGroup style={{ position: "relative" }}>
                    <Label>Endereço de destino</Label>
                    <Input
                      innerRef={inputDestinoRef}
                      type="text"
                      placeholder="Digite o endereço completo"
                      value={enderecoDestino}
                      onChange={handleEnderecoDestinoChange}
                      onFocus={() => setMostrarSugestoesDestino(true)}
                      onBlur={() => {
                        // Delay para permitir clique nas sugestões
                        setTimeout(() => setMostrarSugestoesDestino(false), 200);
                      }}
                    />
                    {mostrarSugestoesDestino && sugestoesDestino.length > 0 && (
                      <ListGroup
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          maxHeight: "200px",
                          overflowY: "auto",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          backgroundColor: "white",
                        }}
                      >
                        {sugestoesDestino.map((sugestao, index) => (
                          <ListGroupItem
                            key={index}
                            action
                            onClick={() => selecionarSugestaoDestino(sugestao)}
                            style={{ cursor: "pointer", padding: "8px 12px" }}
                          >
                            <div style={{ fontSize: "14px", fontWeight: "500" }}>
                              {sugestao.structured_formatting?.main_text || sugestao.description}
                            </div>
                            <div style={{ fontSize: "12px", color: "#666" }}>
                              {sugestao.structured_formatting?.secondary_text || ""}
                            </div>
                          </ListGroupItem>
                        ))}
                      </ListGroup>
                    )}
                  </FormGroup>
                )}

                {/* Botões */}
                <div className="mt-4">
                  <Button
                    color="primary"
                    onClick={montarRota}
                    disabled={loading}
                    block
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Calculando...
                      </>
                    ) : (
                      "Montar/Pré-visualizar Rota"
                    )}
                  </Button>

                  <Button
                    color="success"
                    onClick={registrarRota}
                    disabled={!rotaCalculada || salvandoRota}
                    block
                    className="mt-2"
                  >
                    {salvandoRota ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Registrando...
                      </>
                    ) : (
                      "Registrar Rota"
                    )}
                  </Button>
                </div>

                {/* Seção de Dados para Registro - aparece após rota calculada */}
                {rotaCalculada && (
                  <div className="mt-4 pt-3 border-top">
                    <h6 className="mb-3">Dados para Registro</h6>

                    {/* Tempo estimado */}
                    <div className="mb-3 p-2 bg-light rounded">
                      <strong>Tempo estimado: {formatarTempo(tempoEstimadoTotal)}</strong>
                      <br />
                      <small className="text-muted">
                        Deslocamento + {oportunidadesSelecionadasState.reduce((acc, op) => {
                          const ids = (imoveisSelecionadosPorOp[op.id] && imoveisSelecionadosPorOp[op.id].length)
                            ? imoveisSelecionadosPorOp[op.id]
                            : (op.imoveis || []).map(i => i.id);
                          return acc + ids.length;
                        }, 0)} visita{oportunidadesSelecionadasState.reduce((acc, op) => {
                          const ids = (imoveisSelecionadosPorOp[op.id] && imoveisSelecionadosPorOp[op.id].length)
                            ? imoveisSelecionadosPorOp[op.id]
                            : (op.imoveis || []).map(i => i.id);
                          return acc + ids.length;
                        }, 0) > 1 ? 's' : ''} (60min cada)
                      </small>
                    </div>


                    {/* Seleção de imóvel por oportunidade */}
                    <div className="mb-3">
                      <h6 className="mb-2">Imóveis a visitar</h6>
                      {oportunidadesSelecionadasState.map((op, idx) => (
                        <div key={op.id} className="mb-3 p-3 border rounded">
                          <div className="mb-2">
                            <strong>{idx + 1}. {op.cliente?.nome || 'Cliente'}</strong>
                            <br />
                            <small className="text-muted">{op.nome || 'Oportunidade'}</small>
                          </div>
                          <div className="pl-2">
                            {op.imoveis.map((im) => {
                              const selecionados = imoveisSelecionadosPorOp[op.id] || (op.imoveis || []).map(i => i.id);
                              const checked = selecionados.includes(im.id);
                              const uniqueId = `imovel-${op.id}-${im.id}`;
                              return (
                                <div key={im.id} className="custom-control custom-checkbox mb-2">
                                  <input
                                    className="custom-control-input"
                                    id={uniqueId}
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => onToggleImovel(op.id, im.id, e.target.checked)}
                                  />
                                  <label className="custom-control-label" htmlFor={uniqueId}>
                                    <span className="font-weight-600">{im.nome || `Imóvel ${im.id}`}</span>
                                    <br />
                                    <small className="text-muted">
                                      Lat: {parseFloat(im.latitude).toFixed(5)}, Lng: {parseFloat(im.longitude).toFixed(5)}
                                    </small>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Colaborador responsável */}
                    <FormGroup>
                      <Label>Colaborador responsável *</Label>
                      <Input
                        type="select"
                        value={colaboradorSelecionado}
                        onChange={(e) => setColaboradorSelecionado(e.target.value)}
                        disabled={loadingColaboradores}
                      >
                        <option value="">
                          {loadingColaboradores ? "Carregando..." : "Selecione um colaborador"}
                        </option>
                        {colaboradores.map((colab) => (
                          <option key={colab.id} value={colab.id}>
                            {colab.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>

                    {/* Data de início */}
                    <FormGroup>
                      <Label>Data de início *</Label>
                      <Input
                        type="date"
                        value={dataInicio}
                        onChange={(e) => setDataInicio(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </FormGroup>

                    {/* Data final */}
                    <FormGroup>
                      <Label>Data final</Label>
                      <Input
                        type="date"
                        value={dataFim}
                        onChange={(e) => setDataFim(e.target.value)}
                        min={dataInicio || new Date().toISOString().split('T')[0]}
                      />
                      <small className="text-muted">
                        Opcional - se não informada, será igual à data de início
                      </small>
                    </FormGroup>

                    {/* Observações */}
                    <FormGroup>
                      <Label>Observações</Label>
                      <Input
                        type="textarea"
                        rows="3"
                        placeholder="Observações sobre a rota (opcional)"
                        value={observacoesRota}
                        onChange={(e) => setObservacoesRota(e.target.value)}
                        maxLength="1000"
                      />
                      <small className="text-muted">
                        {observacoesRota.length}/1000 caracteres
                      </small>
                    </FormGroup>
                  </div>
                )}
              </div>
            </Col>

            {/* Mapa */}
            <Col md="8">
              <div style={{ height: "500px" }}>
                <MapComponent
                  directions={directions}
                  markers={markers}
                  center={center}
                  zoom={zoom}
                />
              </div>
            </Col>
          </Row>
        )}
      </ModalBody>
    </Modal>
  );
}
